import { normalizeId } from './ids';
import { normalizeRange, rangeFromSearch, rangeToSearch } from './chartRanges';

/**
 * A dashboard's layout and controls, pure (charts A6; keepr-api SCHEMA
 * "Dashboards"; plan §4.5, §6.5). No React, no HTTP.
 *
 * THE LAYOUT. A 12-column grid of tiles in rows that wrap; the order of
 * `tiles` is the layout. A tile is `w` columns wide — a quarter, a third, a
 * half, two thirds or full — and `h` rows tall (short or tall; a heading is
 * short only). Arrange edits a DRAFT of the tiles and the controls, saved
 * whole on Done (ruled d17).
 *
 * THE CONTROLS. One date range and one filter over every chart. The URL wins
 * (`?range=<preset>`, `?from=&to=`, `?range=own` for each chart's own dates,
 * `?q=<filter>` — present but empty is "no filter"), else the dashboard's
 * stored `controls`, so a filtered dashboard is a link. A choice equal to the
 * stored one leaves the URL clean.
 */

/** Where a dashboard is read: its page, in the context collection. */
export const dashboardPath = (collectionId, dashboardId) => `/collections/${collectionId}/dashboards/${dashboardId}`;
/** Where a new dashboard is named. */
export const newDashboardPath = (collectionId) => `/collections/${collectionId}/dashboards/new`;

/** The widths a tile may take, in the words Arrange's Size offers. */
export const TILE_WIDTHS = Object.freeze([
	{ w: 3, label: 'A quarter' },
	{ w: 4, label: 'A third' },
	{ w: 6, label: 'A half' },
	{ w: 8, label: 'Two thirds' },
	{ w: 12, label: 'Full' },
]);
/** The heights a chart tile may take (a heading is short only). */
export const TILE_HEIGHTS = Object.freeze([
	{ h: 1, label: 'Short' },
	{ h: 2, label: 'Tall' },
]);
/** A chart placed by Add chart or Add to dashboard…: half the width, short. */
export const NEW_CHART_TILE = Object.freeze({ w: 6, h: 1 });
/** A heading added by Add heading: the full width, one row. */
export const NEW_TEXT_TILE = Object.freeze({ w: 12, h: 1 });
/** The longest heading or note (keepr-api dashboardUtils MAX_MARKDOWN). */
export const MAX_MARKDOWN = 2000;
/** The server's name limits (dashboardUtils MAX_NAME, MAX_DESCRIPTION). */
export const DASHBOARD_NAME_MAX = 100;
export const DASHBOARD_DESCRIPTION_MAX = 500;

const RANGE_OWN = 'own';
const NO_CONTROLS = Object.freeze({ range: null, filter: '' });
/** What a dashboard's run says once it stopped re-running on an idle tab (plan §5.5) — the dashboard page and the Overview. */
export const PAUSED = 'Paused. Refresh to update.';
/** The one message when the server cannot read a dashboard's filter: its sentence follows. */
export const FILTER_UNREADABLE = "This filter can't be read";
/** The longest filter a run takes (keepr-api kql MAX_QUERY_LENGTH): a longer `?q=` is never sent. */
export const MAX_FILTER_LENGTH = 2000;
/** What the page says of a `?q=` too long to send (with FILTER_UNREADABLE). */
export const FILTER_TOO_LONG = `A filter is at most ${MAX_FILTER_LENGTH.toLocaleString('en-US')} characters, so the dashboard's own filter is shown instead.`;

/** A dashboard's stored controls, read safely: `{ range: range | null, filter: '' | text }`. */
export function storedControls(dashboard) {
	const controls = dashboard && dashboard.controls;
	if (!controls || typeof controls !== 'object') return NO_CONTROLS;
	const range = normalizeRange(controls.range);
	return {
		range: range === undefined ? null : range,
		filter: typeof controls.filter === 'string' ? controls.filter : '',
	};
}

/**
 * What the URL says of the controls: `{ range, filter }`, each undefined
 * when the URL says nothing of it. `range` null is "each chart's own dates".
 * A `?q=` longer than a run takes is no override (the second review's L7: it
 * went out once per chart and the body limit refused every tile): `filter`
 * is undefined and `tooLong` says why, for the page's one message.
 */
export function controlsFromSearch(searchParams) {
	const get = (key) => (searchParams && typeof searchParams.get === 'function' ? searchParams.get(key) : null);
	const raw = get('range');
	const range = raw === RANGE_OWN ? null : rangeFromSearch(searchParams);
	const q = get('q');
	if (q !== null && q.trim().length > MAX_FILTER_LENGTH) return { range, filter: undefined, tooLong: true };
	return { range, filter: q === null ? undefined : q };
}

/** The controls in force: the URL's where it says something, else the stored ones. */
export function effectiveControls(stored, fromUrl) {
	const base = stored || NO_CONTROLS;
	return {
		range: fromUrl && fromUrl.range !== undefined ? fromUrl.range : base.range,
		filter: fromUrl && fromUrl.filter !== undefined ? fromUrl.filter : base.filter,
	};
}

const sameRange = (a, b) => JSON.stringify(normalizeRange(a) ?? null) === JSON.stringify(normalizeRange(b) ?? null);

/** Do two controls ask the same? */
export function sameControls(a, b) {
	const x = a || NO_CONTROLS;
	const y = b || NO_CONTROLS;
	return sameRange(x.range, y.range) && (x.filter || '').trim() === (y.filter || '').trim();
}

/**
 * Only the controls keys `next` changes from `base`: `{ range? , filter? }`,
 * empty when it asks the same. What a save sends as `controls` (the server
 * replaces only the keys sent), so a range the person changed never carries a
 * filter they did not touch over a colleague's newer one (the second review's
 * L12).
 */
export function changedControls(next, base) {
	const x = next || NO_CONTROLS;
	const y = base || NO_CONTROLS;
	const out = {};
	if (!sameRange(x.range, y.range)) out.range = normalizeRange(x.range) ?? null;
	if ((x.filter || '').trim() !== (y.filter || '').trim()) out.filter = (x.filter || '').trim();
	return out;
}

/**
 * A copy of the URL's params with `next` written over the controls: each
 * equal to its stored value is cleared, every other is written.
 */
export function controlsToSearch(searchParams, next, stored) {
	const base = stored || NO_CONTROLS;
	let out = new URLSearchParams(searchParams);
	if (next && Object.prototype.hasOwnProperty.call(next, 'range')) {
		out = rangeToSearch(out, undefined);
		if (!sameRange(next.range, base.range)) {
			if (next.range === null) out.set('range', RANGE_OWN);
			else out = rangeToSearch(out, next.range);
		}
	}
	if (next && Object.prototype.hasOwnProperty.call(next, 'filter')) {
		const text = typeof next.filter === 'string' ? next.filter.trim() : '';
		if (text === (base.filter || '').trim()) out.delete('q');
		else out.set('q', text);
	}
	return out;
}

/** The URL with every control param removed (Done: the dashboard as saved). */
export function withoutControls(searchParams, keys = { range: true, filter: true }) {
	let out = new URLSearchParams(searchParams);
	if (keys.range) out = rangeToSearch(out, undefined);
	if (keys.filter) out.delete('q');
	return out;
}

// ------------------------------------------------------------------ tiles

/** A tile id the dashboard does not hold yet (1–32 of A-Za-z0-9_-, as the server takes). */
export function newTileId(tiles = [], random = Math.random) {
	const taken = new Set((tiles || []).map((tile) => tile && tile.id));
	for (let i = 0; i < 50; i += 1) {
		const id = `t${Math.floor(random() * 0xffffffffff).toString(16).padStart(10, '0')}`;
		if (!taken.has(id)) return id;
	}
	return `t${Date.now().toString(16)}${taken.size}`;
}

/** The tiles with one moved by `delta` places, clamped (Move up / Move down; SortableList's moveId on tiles). */
export function moveTile(tiles, id, delta) {
	const from = tiles.findIndex((tile) => tile.id === id);
	if (from < 0) return tiles;
	const to = Math.max(0, Math.min(tiles.length - 1, from + delta));
	if (to === from) return tiles;
	const next = [...tiles];
	const [moved] = next.splice(from, 1);
	next.splice(to, 0, moved);
	return next;
}

/** The tiles in the order `ids` names (a drag's new order). */
export function reorderTiles(tiles, ids) {
	const byId = new Map(tiles.map((tile) => [tile.id, tile]));
	const out = ids.map((id) => byId.get(id)).filter(Boolean);
	return out.length === tiles.length ? out : tiles;
}

/** One tile changed: its width, or (a chart's only) its height. */
export function patchTile(tiles, id, patch) {
	return tiles.map((tile) => {
		if (tile.id !== id) return tile;
		const next = { ...tile };
		if (patch.w !== undefined && TILE_WIDTHS.some((o) => o.w === patch.w)) next.w = patch.w;
		if (patch.h !== undefined && tile.kind === 'chart' && TILE_HEIGHTS.some((o) => o.h === patch.h)) next.h = patch.h;
		return next;
	});
}

export const removeTile = (tiles, id) => tiles.filter((tile) => tile.id !== id);

/** A chart placed at the end: available, with the chart the picker offered, so it runs before the save. */
export function addChartTile(tiles, chart) {
	const tile = {
		id: newTileId(tiles),
		kind: 'chart',
		chart_id: normalizeId(chart && chart._id),
		...NEW_CHART_TILE,
		available: true,
		chart,
	};
	return [...tiles, tile];
}

/** A heading or a note placed at the end. */
export function addTextTile(tiles, markdown) {
	return [...tiles, { id: newTileId(tiles), kind: 'text', markdown: `${markdown}`.trim(), ...NEW_TEXT_TILE }];
}

/** The draft Arrange starts from: the dashboard's tiles and its stored controls. */
/**
 * A new draft of `dashboard` (the second review's F1, F3): its tiles; the
 * controls IN FORCE when Arrange began (`inForce` — the link's view where it
 * has one, else the stored defaults), kept as `seedControls` so Done can tell
 * whether the person touched them; and the `version` it was arranged from,
 * which Done states whatever the page has adopted since (a rename answers the
 * dashboard as stored now).
 */
export function draftOf(dashboard, inForce) {
	const controls = inForce || storedControls(dashboard);
	return {
		tiles: Array.isArray(dashboard && dashboard.tiles) ? dashboard.tiles : [],
		controls,
		seedControls: controls,
		version: dashboard && Number.isInteger(dashboard.version) ? dashboard.version : 1,
	};
}

const layoutOf = (tiles) => JSON.stringify((Array.isArray(tiles) ? tiles : []).map((t) => [t.id, t.kind, t.chart_id || null, t.markdown || null, t.w, t.h]));

/** Did the draft's tiles change from the dashboard's? */
export function draftTilesChanged(draft, dashboard) {
	if (!draft || !dashboard) return false;
	return layoutOf(draft.tiles) !== layoutOf(dashboard.tiles);
}

/** Did the person touch the controls while arranging? */
export function draftControlsTouched(draft) {
	return Boolean(draft) && !sameControls(draft.controls, draft.seedControls);
}

/** Is the draft the dashboard as it was when Arrange began? (The layout and the controls; the read side is not compared.) */
export function draftIsClean(draft, dashboard) {
	if (!draft || !dashboard) return true;
	return !draftTilesChanged(draft, dashboard) && !draftControlsTouched(draft);
}

/**
 * The charts a dashboard may hold (the tier rule, ⚑ d08): one for everyone
 * holds only this collection's charts for everyone (never a collection
 * above's, KPR-1084); a personal one holds those and its owner's own charts
 * (only the owner arranges a personal dashboard, so "yours" are the caller's).
 */
export function placeableCharts(dashboard, { charts = [] } = {}) {
	const forEveryone = dashboard && dashboard.scope === 'collection';
	return (charts || []).filter((chart) => chart && (chart.scope === 'collection' || !forEveryone));
}
